import {useEffect, useState} from 'react';

import {fileService, kycBucketKey} from '@/services/fileService';
import type {AdminAfriexPerson} from '@/types';
import {renderFile} from '@/utils/imageFiles';
type KYCRequestedDcumentsProps = {
  user: AdminAfriexPerson;
};

// The only reason `documents` was memoized was to stop the `{}` fallback from
// getting a fresh identity every render and re-firing the fetch effect below.
// A module-level constant gives that same stability without a memo whose
// inferred and written dependencies disagree.
const NO_DOCUMENTS: NonNullable<
  NonNullable<AdminAfriexPerson['kyc']>['complianceDocuments']
> = {};

const KYCRequestedDcuments = ({user}: KYCRequestedDcumentsProps) => {
  const [fileUrls, setFileUrls] = useState<{[key: string]: string}>({});

  const documents = user?.kyc?.complianceDocuments ?? NO_DOCUMENTS;

  useEffect(() => {
    const fetchAllUrls = async () => {
      const urls: {[key: string]: string} = {};
      for (const [docType, fileName] of Object.entries(documents)) {
        if (typeof fileName === 'string') {
          try {
            const url = await fileService.fetchUploadedFile(
              fileName,
              kycBucketKey,
            );
            urls[docType] = url;
          } catch (error) {
            console.error(`Failed to fetch URL for ${docType}:`, error);
          }
        }
      }
      setFileUrls(urls);
    };

    // Fire-and-forget: fetchAllUrls try/catches every per-document fetch and
    // logs it, so this only stops an unexpected throw becoming an unhandled
    // rejection.
    fetchAllUrls().catch(() => {});
  }, [documents]);
  return (
    <div className="px-3 pt-3 my-8">
      <h3 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
        {'Requested Documents'.toLocaleUpperCase()}
      </h3>
      <hr className="mb-5 dark:border-dark-borderMid" />

      <div className="mb-2 py-2 flex flex-col gap-3 justify-between">
        <p className="text-sm font-medium items-center gap-4 flex text-gray-700 dark:text-gray-100">
          {user.kyc?.requestedAdditionalDocuments?.map((title, index) => (
            <span key={index}>
              [
              {title
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (char) => char.toUpperCase())}
              ]{' '}
            </span>
          ))}
        </p>

        <div className="flex flex-wrap gap-1">
          {Object.keys(fileUrls).length > 0 ? (
            Object.entries(fileUrls).map(([docType, url]) => (
              <div key={docType} className="w-1/4 pr-2">
                {renderFile(url, docType)}
              </div>
            ))
          ) : (
            <p>No documents requested</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default KYCRequestedDcuments;
